'use client';

import Image from 'next/image';
import { testimonials } from '@/static/testimonials';
import { useEffect, useState, useRef } from 'react';

const Testimonials: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const [isMobile, setIsMobile] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isHolding, setIsHolding] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [touchEndX, setTouchEndX] = useState<number | null>(null);

  const extendedTestimonials = [
    testimonials[testimonials.length - 1],
    ...testimonials,
    testimonials[0],
  ];

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!isMobile) return;
    if (isAnimating) return;
    if (isHolding) return;
    const interval = setInterval(() => {
      setIsAnimating(true);
      setCurrentSlide(prev => prev + 1);
    }, 3000);

    return () => clearInterval(interval);
  }, [isMobile, isAnimating, isHolding]);

  useEffect(() => {
    if (!isMobile) return;
    if (!isAnimating) return;
    if (currentSlide === 0) {
      setTimeout(() => {
        setIsAnimating(false);
        setCurrentSlide(testimonials.length);
      }, 500);
    } else if (currentSlide === testimonials.length + 1) {
      setTimeout(() => {
        setIsAnimating(false);
        setCurrentSlide(1);
      }, 500);
    } else {
      setTimeout(() => setIsAnimating(false), 500);
    }
  }, [currentSlide, isMobile, isAnimating, testimonials.length]);

  const goToSlide = (idx: number) => {
    setIsAnimating(true);
    setCurrentSlide(idx + 1);
  };

  // Swipe handlers
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsHolding(true);
    setTouchStartX(e.touches[0].clientX);
    setTouchEndX(null);
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    setIsHolding(false);
    if (touchStartX !== null && touchEndX !== null) {
      const distance = touchStartX - touchEndX;

      if (Math.abs(distance) > 50) {
        // threshold
        if (distance > 0) {
          // Swipe left
          setIsAnimating(true);
          setCurrentSlide(prev => prev + 1);
        } else {
          // Swipe right
          setIsAnimating(true);
          setCurrentSlide(prev => prev - 1);
        }
      }
    }
    setTouchStartX(null);
    setTouchEndX(null);
  };

  if (isMobile) {
    return (
      <div
        ref={sliderRef}
        className="relative w-full overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={() => setIsHolding(true)}
        onMouseUp={() => setIsHolding(false)}
        onMouseLeave={() => setIsHolding(false)}
      >
        <div
          className="flex justify-start gap-[15px] pr-[10vw] pl-[10vw] transition-transform duration-500 ease-in-out"
          style={{
            width: `calc(${extendedTestimonials.length} * 80vw + ${(extendedTestimonials.length - 1) * 15}px + 20vw)`,
            transform: `translateX(calc(-${currentSlide} * (80vw + 15px)))`,
          }}
        >
          {extendedTestimonials.map((testimonial, index) => (
            <div key={index} className="w-[80vw] max-w-[80vw] flex-shrink-0">
              <div className="h-full rounded-[48px] border border-gray-200 bg-white p-6">
                <div className="mb-4 flex items-center">
                  <Image
                    src={testimonial.avatar}
                    alt={`${testimonial.name} avatar`}
                    width={50}
                    height={50}
                    className="rounded-full"
                  />
                  <div className="ml-4">
                    <h3 className="text-lg font-semibold text-gray-800">
                      {testimonial.name}
                    </h3>
                    <p className="text-sm text-gray-600">{testimonial.role}</p>
                  </div>
                </div>
                <p className="text-sm leading-relaxed text-gray-700 md:text-base">
                  &quot;{testimonial.message}&quot;
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="absolute bottom-[-20px] left-1/2 flex -translate-x-1/2 space-x-2">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`h-2 w-2 rounded-full ${currentSlide === index + 1 ? 'bg-primary' : 'bg-gray-300'}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:mt-10 md:grid-cols-2 md:px-[50px] lg:mt-0 lg:grid-cols-3 lg:gap-8 lg:px-0">
      {testimonials.map((testimonial, index) => (
        <div
          key={index}
          className="h-full rounded-[48px] border border-gray-200 bg-white p-6"
        >
          <div className="mb-4 flex items-center">
            <Image
              src={testimonial.avatar}
              alt={`${testimonial.name} avatar`}
              width={50}
              height={50}
              className="rounded-full"
            />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-800">
                {testimonial.name}
              </h3>
              <p className="text-[12px] text-gray-600">{testimonial.role}</p>
            </div>
          </div>
          <p className="text-[14px] leading-relaxed text-gray-700">
            &quot;{testimonial.message}&quot;
          </p>
        </div>
      ))}
    </div>
  );
};

export default Testimonials;
